import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Setting, Client } from '../../types';
import RichTextEditor from './RichTextEditor';
import './SettingsList.css';

interface SettingsListProps {
  clientId?: number | null;
}

const SettingsList: React.FC<SettingsListProps> = ({ clientId }) => {
  const { user, isAdmin } = useAuth();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(clientId || null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<{ [key: number]: boolean }>({});
  const [errors, setErrors] = useState<{ [key: number | string]: string }>({});
  const [showVariablesPopup, setShowVariablesPopup] = useState(false);

  /**
   * Format setting key for display
   * Replaces underscores with spaces and capitalizes each word
   * Example: "email_content" -> "Email Content"
   */
  const formatSettingKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get<Client[]>('/clients');
        setClients(response.data);
        if (response.data.length > 0 && !selectedClientId) {
          setSelectedClientId(response.data[0].id);
          setSelectedClient(response.data[0]);
        } else if (selectedClientId) {
          const client = response.data.find(c => c.id === selectedClientId);
          if (client) setSelectedClient(client);
        }
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      }
    };

    const fetchSettings = async () => {
      if (!selectedClientId && isAdmin()) return;
      
      try {
        setLoading(true);
        const url = selectedClientId 
          ? `/settings?client_id=${selectedClientId}`
          : '/settings';
        const response = await api.get<Setting[]>(url);
        const sorted = [...response.data].sort((a, b) => {
          const aContent = a.key.toLowerCase().includes('content');
          const bContent = b.key.toLowerCase().includes('content');
          if (aContent === bContent) return 0;
          return aContent ? 1 : -1;
        });
        setSettings(sorted);
        setErrors({});
      } catch (error: any) {
        console.error('Failed to fetch settings:', error);
        if (error.response?.status === 403) {
          setErrors({ general: 'You do not have access to these settings.' });
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin()) {
      fetchClients();
    }
    if (selectedClientId || !isAdmin()) {
      fetchSettings();
    }
  }, [selectedClientId, isAdmin]);

  useEffect(() => {
    if (!isAdmin() && user?.clients && user.clients.length > 0) {
      // For client users, use their first associated client
      setSelectedClientId(user.clients[0].id);
      setSelectedClient(user.clients[0]);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    if (selectedClientId) {
      const client = clients.find(c => c.id === selectedClientId);
      if (client) setSelectedClient(client);
    }
  }, [selectedClientId, clients]);

  const handleUpdate = async (setting: Setting, newValue: string) => {
    if (setting.key === 'review_threshold') {
      const numValue = parseFloat(newValue);
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        setErrors({ ...errors, [setting.id]: 'Review threshold must be a number between 0 and 100' });
        return;
      }
    }
    if (setting.key === 'comment_count') {
      const numValue = parseInt(newValue, 10);
      if (newValue !== '' && (isNaN(numValue) || numValue < 0)) {
        setErrors({ ...errors, [setting.id]: 'Comment count must be a non-negative number' });
        return;
      }
    }

    try {
      setSaving({ ...saving, [setting.id]: true });
      setErrors({ ...errors, [setting.id]: '' });
      console.log(newValue);

      const response = await api.put<Setting>(`/settings/${setting.id}`, {
        value: newValue,
      });

      setSettings(settings.map(s => s.id === setting.id ? response.data : s));
    } catch (error: any) {
      console.error('Failed to update setting:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.value?.[0] || 'Failed to update setting';
      setErrors({
        ...errors,
        [setting.id]: errorMessage,
      });
    } finally {
      setSaving({ ...saving, [setting.id]: false });
    }
  };

  const handleInputChange = (settingId: number, value: string) => {
    setSettings(settings.map(s => 
      s.id === settingId ? { ...s, value } : s
    ));
  };

  const getStorageUrl = (path: string) => {
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '') || window.location.origin;
    return `${base}/storage/${path}`;
  };

  const handleLogoFileChange = async (setting: Setting, file: File | null) => {
    if (!file || !selectedClientId) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setErrors({ ...errors, [setting.id]: 'Only JPG and PNG files are allowed for company logo' });
      return;
    }
    const maxSize = 512 * 1024;
    if (file.size > maxSize) {
      setErrors({ ...errors, [setting.id]: 'Logo must be under 512 KB.' });
      return;
    }
    setErrors({ ...errors, [setting.id]: '' });
    setSaving({ ...saving, [setting.id]: true });
    try {
      const formData = new FormData();
      formData.append('client_id', String(selectedClientId));
      formData.append('logo', file);
      const response = await api.post<Setting>('/settings/client-logo/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSettings(settings.map(s => s.id === setting.id ? response.data : s));
    } catch (err: any) {
      setErrors({
        ...errors,
        [setting.id]: err.response?.data?.message || err.response?.data?.errors?.logo?.[0] || 'Upload failed',
      });
    } finally {
      setSaving(prev => ({ ...prev, [setting.id]: false }));
    }
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  if (!isAdmin() && (!user?.clients || user.clients.length === 0)) {
    return <div className="no-data">No companies associated with your account.</div>;
  }

  const globalVariables = [
    { variable: '{customer_name}', description: 'Customer\'s name from the review' },
    { variable: '{client_name}', description: 'Company name' },
    { variable: '{company_name}', description: 'Company name (alias for client_name)' },
    { variable: '{review_rating}', description: 'The rating given in the review (0-10)' },
    { variable: '{review_comment}', description: 'The comment text from the review' },
    { variable: '{review_date}', description: 'Date when the review was submitted' },
  ];

  return (
    <div className="settings-list">
      {selectedClient && (
        <div className="company-name-header">
          <h2>Viewing Settings for: {selectedClient.client_name}</h2>
        </div>
      )}
      <div className="settings-actions">
        <button
          onClick={() => setShowVariablesPopup(true)}
          className="btn btn-secondary"
          style={{ marginBottom: '20px' }}
        >
          View Available Variables
        </button>
      </div>
      {isAdmin() && clients.length > 0 && (
        <div className="settings-filter">
          <label htmlFor="client-select">Select Company:</label>
          <select
            id="client-select"
            value={selectedClientId || ''}
            onChange={(e) => setSelectedClientId(Number(e.target.value) || null)}
            className="client-select"
          >
            <option value="">All Companies</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.client_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {errors.general && (
        <div className="alert alert-error">{errors.general}</div>
      )}

      {settings.length === 0 ? (
        <div className="no-data">No settings found for this company.</div>
      ) : (
        <div className="settings-table-container">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {settings.map((setting) => (
                <tr key={setting.id}>
                  <td className="setting-key">
                    <span className="key-label">{formatSettingKey(setting.key)}</span>
                  </td>
                  <td className="setting-value">
                    {setting.key.includes('content') ? (
                      <RichTextEditor
                        value={setting.value || ''}
                        onChange={(value) => handleInputChange(setting.id, value)}
                        placeholder="Enter content"
                      />
                    ) : setting.key === 'review_threshold' ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={setting.value || ''}
                        onChange={(e) => handleInputChange(setting.id, e.target.value)}
                        className="setting-input"
                        placeholder="Enter rating threshold (0-100)"
                      />
                    ) : setting.key === 'comment_count' ? (
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={setting.value ?? ''}
                        onChange={(e) => handleInputChange(setting.id, e.target.value)}
                        className="setting-input"
                        placeholder="Enter comment count"
                      />
                    ) : setting.key === 'client_logo' ? (
                      <div className="setting-input-file">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            handleLogoFileChange(setting, f || null);
                            e.target.value = '';
                          }}
                          className="setting-file-input"
                          disabled={saving[setting.id]}
                        />
                        {setting.value && (
                          <div className="client-logo-preview">
                            <img src={getStorageUrl(setting.value)} alt="Company logo" />
                          </div>
                        )}
                        <small className="setting-help-text">
                          Upload a small logo (JPG or PNG, max 512 KB). It will appear in the header.
                        </small>
                      </div>
                    ) : ['last_service', 'quality_service', 'refer_business', 'work_again'].includes(setting.key) ? (
                      <input
                        type="text"
                        value={setting.value || ''}
                        onChange={(e) => handleInputChange(setting.id, e.target.value)}
                        className="setting-input"
                        placeholder="Enter text"
                      />
                    ) : (
                      <input
                        type="url"
                        value={setting.value || ''}
                        onChange={(e) => handleInputChange(setting.id, e.target.value)}
                        className="setting-input"
                        placeholder="Enter URL"
                      />
                    )}
                  </td>
                  <td className="setting-actions">
                    {setting.key !== 'client_logo' && (
                      <button
                        onClick={() => handleUpdate(setting, setting.value || '')}
                        disabled={saving[setting.id]}
                        className="btn btn-primary btn-sm"
                      >
                        {saving[setting.id] ? 'Saving...' : 'Save'}
                      </button>
                    )}
                    {setting.key === 'client_logo' && saving[setting.id] && (
                      <span className="saving-text">Uploading...</span>
                    )}
                    {errors[setting.id] && (
                      <div className="error-message">{errors[setting.id]}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showVariablesPopup && (
        <div className="modal-overlay" onClick={() => setShowVariablesPopup(false)}>
          <div className="modal-content variables-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Available Global Variables</h3>
              <button onClick={() => setShowVariablesPopup(false)} className="close-button">×</button>
            </div>
            <div className="variables-list">
              <p>You can use these variables in your text boxes. They will be replaced with actual values when used.</p>
              <table className="variables-table">
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {globalVariables.map((item, index) => (
                    <tr key={index}>
                      <td><code>{item.variable}</code></td>
                      <td>{item.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsList;
